/**
 * Typed error thrown by all client API fetch helpers (throwIfResNotOk,
 * jsonOrThrow) so programmatic callers can read the HTTP status and the
 * parsed JSON body directly, instead of having to re-parse the message
 * string.
 *
 * Contract:
 *   err.message  — "NNN: human-readable text"  (friendly, shown in toasts)
 *   err.status   — HTTP status code (number)
 *   err.body     — parsed JSON body, or null if the body was not JSON
 *   err.rawBody  — original response text (used in admin error logs)
 *
 * The "NNN: " prefix is intentionally preserved so legacy code that
 * does `error.message.startsWith("409")` or regex-splits the string
 * continues to work without modification.
 */
export class ApiError extends Error {
  readonly status: number;
  /** Parsed JSON response body, or null when the body was not valid JSON. */
  readonly body: any;
  /** Raw (unparsed) response text — used by admin error logs. */
  readonly rawBody: string;

  constructor(status: number, friendly: string, body: any, rawBody: string) {
    super(`${status}: ${friendly}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.rawBody = rawBody;
  }
}

/**
 * Parse a response body text into a human-readable message and a
 * structured body object.
 *
 * The global error handler emits `{ message, error }`.  Route-level
 * handlers emit `{ error }`.  Structured error bodies may also include
 * fields like `code`, `reason`, and `missing` used by the transcription
 * error branches in call-context — those are preserved untouched on the
 * returned `body` so callers can read them.
 *
 * Falls back to the raw text when the body is not JSON.
 */
export function extractFriendly(
  text: string,
  fallback: string,
): { friendly: string; body: any } {
  const raw = text || fallback;
  try {
    const b = JSON.parse(raw);
    if (b && typeof b === 'object' && (b.message || b.error)) {
      return { friendly: String(b.message || b.error), body: b };
    }
    // Valid JSON but no message/error key — return raw text, keep body.
    return { friendly: raw, body: b };
  } catch {
    // Not JSON.
    return { friendly: raw, body: null };
  }
}

/**
 * Extract HTTP status + parsed body from a thrown error, preferring the
 * typed ApiError fields over legacy string parsing.
 *
 * This is the canonical implementation of what `parseApiError` in
 * call-context.tsx used to do inline.  By reading the structured fields
 * directly when the error is an ApiError, we avoid re-parsing and ensure
 * that programmatic consumers (body.code, body.missing, body.reason) see
 * the full object even after throwIfResNotOk started emitting friendly
 * plain-text messages.
 *
 * The regex fallback keeps compatibility with any error that is not an
 * ApiError (third-party code, older paths not yet migrated).
 */
export function parseApiErrorObject(e: unknown): { status: number | null; body: any } {
  if (e instanceof ApiError) {
    return { status: e.status, body: e.body };
  }
  // Legacy fallback: errors whose .message was formatted as "NNN: {JSON}"
  // by an older version of throwIfResNotOk or jsonOrThrow.
  const msg: string = (e as any)?.message || '';
  const m = msg.match(/^(\d{3}):\s*([\s\S]*)$/);
  if (!m) return { status: null, body: null };
  let body: any = null;
  try { body = JSON.parse(m[2]); } catch { body = m[2]; }
  return { status: Number(m[1]), body };
}
